import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '@/i18n';
import { Button } from '@/components/Shared/Button';
import { ChoiceTiles } from '@/components/Shared/ChoiceTiles';
import { EmptyState } from '@/components/Shared/EmptyState';
import { FormField } from '@/components/Shared/FormField';
import { Loading } from '@/components/Shared/Loading';
import { TextArea } from '@/components/Shared/TextArea';
import {
  FacilityFlowCoreModulesFleetEntitiesVehicleCheckKind as VehicleCheckKindEnum,
  type FacilityFlowCoreModulesFleetDtosVehicleCheckDto as VehicleCheckDto,
  type FacilityFlowCoreModulesFleetEntitiesVehicleCheckKind as VehicleCheckKind,
} from '@/api/generated/models';
import { useVehicleStore } from '@/store/useVehicleStore';
import VehicleCheckBadges from '@/pages/Vehicles/VehicleCheckBadges';
import { formatDateTime } from '@/utils/dateUtils';
import { VEHICLE_CHECK_KINDS, formatValidUntil, validUntilFromInput } from '@/utils/vehicleChecks';

const MAX_NOTE = 500;

interface VehicleChecksSectionProps {
  vehicleId: string;
}

/**
 * Verbandskasten, Pickerl und Vignette eines Fahrzeugs (FAFD-69): neuen Nachweis eintragen und
 * den Verlauf sehen. Einträge werden nie überschrieben — der neueste je Art gilt.
 */
export default function VehicleChecksSection({ vehicleId }: VehicleChecksSectionProps) {
  const { t } = useTranslation(['vehicles', 'common']);
  const fetchChecks = useVehicleStore((s) => s.fetchChecks);
  const recordCheck = useVehicleStore((s) => s.recordCheck);
  const saving = useVehicleStore((s) => s.saving);

  const [history, setHistory] = useState<VehicleCheckDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [kind, setKind] = useState<VehicleCheckKind>(VehicleCheckKindEnum.FirstAidKit);
  const [validUntil, setValidUntil] = useState('');
  const [note, setNote] = useState('');
  const [dateError, setDateError] = useState<string | undefined>();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      setHistory(await fetchChecks(vehicleId));
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [fetchChecks, vehicleId]);

  useEffect(() => {
    load();
  }, [load]);

  const isInspection = kind === VehicleCheckKindEnum.Inspection;

  const handleRecord = async () => {
    if (!validUntil) {
      setDateError(t('vehicles:checks.validation.dateRequired'));
      return;
    }
    const created = await recordCheck(vehicleId, {
      kind,
      validUntil: validUntilFromInput(kind, validUntil),
      note: note.trim() || null,
    });
    if (!created) return;
    setHistory((h) => [created, ...h]);
    setValidUntil('');
    setNote('');
  };

  const renderHistory = () => {
    if (loading) return <Loading />;
    if (loadFailed) {
      return <EmptyState variant="error" title={t('vehicles:checks.errors.fetchFailed')} onRetry={load} />;
    }
    if (history.length === 0) {
      return <p className="text-[12px] text-slate-500">{t('vehicles:checks.empty')}</p>;
    }
    return (
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
        {history.map((check) => (
          <li key={check.id} className="px-3 py-2 space-y-0.5">
            <div className="flex items-baseline justify-between gap-2 text-[13px]">
              <span className="font-medium text-slate-900">{t(`vehicles:checks.kinds.${check.kind}`)}</span>
              <span className="tabular-nums text-slate-700">
                {t('vehicles:checks.validUntil', { date: formatValidUntil(check.kind, check.validUntil) })}
              </span>
            </div>
            {check.note && <p className="text-[12px] text-slate-700">{check.note}</p>}
            <p className="text-[11px] text-slate-500 tabular-nums">
              {t('vehicles:checks.recordedBy', {
                name: check.recordedBy ?? '–',
                date: formatDateTime(check.recordedAt),
              })}
            </p>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <section className="space-y-4 border-t border-slate-200 pt-5">
      <div className="space-y-2">
        <h3 className="text-[13px] font-semibold text-slate-900">{t('vehicles:checks.title')}</h3>
        <VehicleCheckBadges checks={history} />
      </div>

      <div className="space-y-3">
        <ChoiceTiles
          legend={t('vehicles:checks.fields.kind')}
          compact
          value={kind}
          onChange={(value) => {
            setKind(value);
            setValidUntil('');
            setDateError(undefined);
          }}
          options={VEHICLE_CHECK_KINDS.map((value) => ({ value, label: t(`vehicles:checks.kinds.${value}`) }))}
        />
        <FormField
          label={isInspection ? t('vehicles:checks.fields.dueMonth') : t('vehicles:checks.fields.validUntil')}
          name="validUntil"
          type={isInspection ? 'month' : 'date'}
          required
          value={validUntil}
          onChange={(e) => {
            setValidUntil(e.target.value);
            setDateError(undefined);
          }}
          helperText={isInspection ? t('vehicles:checks.hints.inspection') : undefined}
          error={dateError}
          className="tabular-nums"
        />
        <TextArea
          label={t('vehicles:checks.fields.note')}
          name="note"
          rows={2}
          maxLength={MAX_NOTE}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t('vehicles:checks.placeholders.note')}
        />
        <div className="flex justify-end">
          <Button variant="secondary" size="sm" loading={saving} onClick={handleRecord}>
            {t('vehicles:checks.actions.record')}
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <h4 className="text-[12px] font-medium text-slate-600">{t('vehicles:checks.history')}</h4>
        {renderHistory()}
      </div>
    </section>
  );
}
